/**
 * Drill strategy registry (Phase 3, design §1): one pure strategy per drill
 * kind. The engine looks a strategy up by the resolved kind (D-5).
 */
import type { DrillDrillType } from 'src/types/course/drill';
import type {
  AnyDrillStrategy,
  DrillContext,
  DrillKind,
  DrillStrategyRegistry,
} from './types';
import { vocabularyStrategy } from './vocabulary';
import { pairsStrategy } from './pairs';
import { singleChoiceStrategy } from './singleChoice';
import { multiChoiceStrategy } from './multiChoice';
import { fillInTheBlankStrategy } from './fillInTheBlank';
import { crossMatchStrategy } from './crossMatch';
import { trueFalseStrategy } from './trueFalse';
import { wordBuilderStrategy } from './wordBuilder';
import { memoryFlipStrategy } from './memoryFlip';
import { sentenceBuilderStrategy } from './sentenceBuilder';

export const drillStrategies: DrillStrategyRegistry = {
  Vocabulary: vocabularyStrategy,
  Pairs: pairsStrategy,
  SingleChoice: singleChoiceStrategy,
  MultiChoice: multiChoiceStrategy,
  FillInTheBlank: fillInTheBlankStrategy,
  CrossMatch: crossMatchStrategy,
  TrueFalse: trueFalseStrategy,
  WordBuilder: wordBuilderStrategy,
  MemoryFlip: memoryFlipStrategy,
  SentenceBuilder: sentenceBuilderStrategy,
};

/** The strategy of a drill kind; no kind (Theory/unknown) → null. */
export function getDrillStrategy(
  kind: DrillKind | null,
): AnyDrillStrategy | null {
  if (!kind) return null;
  return drillStrategies[kind] ?? null;
}

/**
 * The context a strategy reads: the drill type and its typing rules, with
 * useStep's defaults (punctuation + capitalization ignored, word order kept).
 */
export function drillContextOf(
  drillType: DrillDrillType | undefined,
): DrillContext {
  return {
    drillType,
    typingRules: {
      ignorePunctuation: drillType?.typing_rules?.ignore_punctuation ?? true,
      ignoreCapitalization:
        drillType?.typing_rules?.ignore_capitalization ?? true,
      ignoreWordOrder: drillType?.typing_rules?.ignore_word_order ?? false,
    },
  };
}
